import React, { useEffect, useState } from 'react';
import { Box, Grid, styled, Typography, Collapse, IconButton, FormControl, MenuItem, TextField, Select, InputAdornment, InputLabel, CircularProgress } from '@mui/material';
import { ToggleOffOutlined, ToggleOnOutlined, ExpandMoreOutlined } from '@mui/icons-material';
import { API } from '../utils/Api';
import { toast } from 'react-toastify';
import Images from '../utils/Images';
import axios from 'axios';
import SearchIcon from '@mui/icons-material/Search';
import AddonRawMaterial from './AddonRawMaterials';

const ExpandIcon = styled(ExpandMoreOutlined)(({ expand }) => ({
    transform: expand ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.2s ease-in-out',
}));

const AddonListing = () => {
    const token = localStorage.getItem('token');
    const [addonsList, setAddonsList] = useState([]);
    const [openMap, setOpenMap] = useState({});
    const [loading, setLoading] = useState(false);
    const storedOutletData = localStorage.getItem('outletData');
    const outletData = JSON.parse(storedOutletData);
    const outletId = outletData?._id;
    const [stockFilter, setStockFilter] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredAddons, setFilteredAddons] = useState([]);

    const toggleOpen = (id) => {
        setOpenMap((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const getAddonsList = async () => {
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const response = await axios.get(`${API.BASE_URL}addons?page=1&limit=1000`, { headers });
            if (response?.data?.status) {
                const addons = response?.data?.data?.addons;
                setAddonsList(addons);
                setFilteredAddons(addons);
            }
            setLoading(false);
        } catch (error) {
            toast.error("Failed to fetch addons", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored"
            });
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleAddonValueForOutlet = async (addon, value = null) => {
        const outletData = JSON.parse(localStorage.getItem('outletData'));
        const outletId = outletData?._id;
        const token = localStorage.getItem('token');

        const isAddonLevel = value === null;

        const isCurrentlyExcluded = isAddonLevel
            ? addon.excludedOutlets?.some((outlet) => outlet._id === outletId)
            : value.excludedOutlets?.some((outlet) => outlet._id === outletId);

        let payload = {
            addonData: {
                _id: addon._id,
                isOutletExcluded: isAddonLevel ? !isCurrentlyExcluded : false
            },
            addonValuesData: []
        };

        if (!isAddonLevel) {
            // Toggling a specific addon value
            payload.addonValuesData.push({
                _id: value._id,
                isOutletExcluded: !isCurrentlyExcluded
            });
        } else if (!payload.addonData.isOutletExcluded) {
            // Allocating the entire addon: required to pass all values
            payload.addonValuesData = addon.addonValues.map(val => ({
                _id: val._id,
                isOutletExcluded: false
            }));
        }

        try {
            const headers = {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            };

            const response = await axios.put(`${API.BASE_URL}addons/include-exclude-outlet-for-addons`, payload, { headers });

            if (response?.data?.status) {
                toast.success('Addon updated successfully', {
                    position: 'top-right',
                    autoClose: 3000,
                    theme: 'colored',
                });
                getAddonsList(); // refresh data
            } else {
                toast.error('Failed to update addon', {
                    position: 'top-right',
                    autoClose: 3000,
                    theme: 'colored',
                });
            }
        } catch (error) {
            let errorMessage = 'Something went wrong';
            if (error?.response) {
                const { status, data } = error.response;
                if ([400, 406, 412, 500].includes(status)) {
                    errorMessage = data?.error?.message || data?.error || data?.message;
                }
            }
            toast.error(errorMessage, {
                position: 'top-right',
                autoClose: 3000,
                theme: 'colored',
            });
        }
    };

    const handleSearch = (searchText = searchQuery, stockStatus = stockFilter) => {
        setSearchQuery(searchText);

        const filtered = addonsList
            .map(addon => {
                const matchesSearch = addon.title?.toLowerCase().includes(searchText.toLowerCase());

                // Filter addon values based on stock status
                let filteredValues = addon.addonValues || [];

                if (stockStatus === 'InStock') {
                    filteredValues = filteredValues.filter(
                        value => !value.excludedOutlets?.some(outlet => outlet._id === outletId)
                    );
                } else if (stockStatus === 'Exhausted') {
                    filteredValues = filteredValues.filter(
                        value => value.excludedOutlets?.some(outlet => outlet._id === outletId)
                    );
                }

                return {
                    ...addon,
                    addonValues: filteredValues,
                    _matchesSearch: matchesSearch,
                };
            })
            .filter(addon =>
                // Keep addons that match search and have visible values
                addon._matchesSearch && addon.addonValues.length > 0
            );

        setFilteredAddons(filtered);
    };

    useEffect(() => {
        getAddonsList();
    }, [])

    return (
        <>
            <Box sx={{ p: { xs: 1.5, md: 3 }, backgroundColor: '#f7f8fb', minHeight: '100vh' }}>

                {/* Header / Filters */}
                <Grid
                    container
                    spacing={2}
                    sx={{
                        mb: 3,
                        p: 2,
                        backgroundColor: '#fff',
                        borderRadius: 3,
                        boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
                        alignItems: 'center',
                    }}
                >
                    <Grid item xs={12} md={3}>
                        <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#222' }}>
                            Stock Inventory
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: '#777', mt: 0.5 }}>
                            Manage addon stock availability
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search addon..."
                            variant="outlined"
                            value={searchQuery}
                            onChange={(e) => handleSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#777' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                backgroundColor: '#fafafa',
                                borderRadius: 2,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Stock Status</InputLabel>
                            <Select
                                value={stockFilter}
                                label="Stock Status"
                                onChange={(e) => {
                                    const newStatus = e.target.value;
                                    setStockFilter(newStatus);
                                    handleSearch(searchQuery, newStatus);
                                }}
                                sx={{ borderRadius: 2 }}
                            >
                                <MenuItem value="All">All</MenuItem>
                                <MenuItem value="InStock">In Stock</MenuItem>
                                <MenuItem value="Exhausted">Out of Stock</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                {/* Table Header */}
                <Grid
                    container
                    sx={{
                        display: { xs: 'none', md: 'flex' },
                        px: 3,
                        py: 1.5,
                        mb: 1,
                        backgroundColor: '#eef1f6',
                        borderRadius: 2,
                    }}
                >
                    <Grid item md={2}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#555' }}>
                            Item No
                        </Typography>
                    </Grid>

                    <Grid item md={6}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#555' }}>
                            Addon
                        </Typography>
                    </Grid>

                    <Grid item md={4} sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#555' }}>
                            Stock Status
                        </Typography>
                    </Grid>
                </Grid>

                {/* Loader */}
                {loading ? (
                    <Box
                        sx={{
                            minHeight: 250,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: '#fff',
                            borderRadius: 3,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : filteredAddons?.length === 0 ? (
                    <Box
                        sx={{
                            p: 5,
                            textAlign: 'center',
                            backgroundColor: '#fff',
                            borderRadius: 3,
                            boxShadow: '0 4px 14px rgba(0,0,0,0.05)',
                        }}
                    >
                        <Typography sx={{ fontSize: 17, fontWeight: 600 }}>
                            No addons found
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: '#777', mt: 1 }}>
                            Try changing your search or stock filter.
                        </Typography>
                    </Box>
                ) : (
                    filteredAddons?.map((addon, index) => {
                        const isAddonExcluded = addon.excludedOutlets?.some(
                            (outlet) => outlet._id === outletId
                        );

                        return (
                            <Grid
                                container
                                key={addon._id}
                                sx={{
                                    mb: 2,
                                    backgroundColor: '#fff',
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    border: '1px solid #edf0f5',
                                    boxShadow: '0 4px 14px rgba(0,0,0,0.05)',
                                    transition: '0.2s ease',
                                    '&:hover': {
                                        boxShadow: '0 8px 22px rgba(0,0,0,0.08)',
                                        transform: 'translateY(-2px)',
                                    },
                                }}
                            >
                                {/* Parent Addon Row */}
                                <Grid
                                    item
                                    container
                                    xs={12}
                                    sx={{
                                        px: { xs: 1.5, md: 3 },
                                        py: 2,
                                        alignItems: 'center',
                                        backgroundColor: '#fff',
                                    }}
                                >
                                    <Grid item xs={2} md={2}>
                                        <Typography
                                            sx={{
                                                width: 34,
                                                height: 34,
                                                borderRadius: '50%',
                                                backgroundColor: '#f0f3f8',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 14,
                                                fontWeight: 700,
                                            }}
                                        >
                                            {index + 1}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={7} md={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            {addon.badgeImage ? (
                                                <Box
                                                    component="img"
                                                    src={addon.badgeImage}
                                                    alt={addon.title}
                                                    sx={{
                                                        width: 48,
                                                        height: 48,
                                                        borderRadius: 2,
                                                        objectFit: 'cover',
                                                        border: '1px solid #eee',
                                                    }}
                                                />
                                            ) : (
                                                <Box
                                                    sx={{
                                                        width: 48,
                                                        height: 48,
                                                        borderRadius: 2,
                                                        backgroundColor: '#f0f3f8',
                                                    }}
                                                />
                                            )}

                                            <Box>
                                                <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                                                    {addon.title}
                                                </Typography>

                                                <Box
                                                    sx={{
                                                        mt: 0.6,
                                                        display: 'inline-flex',
                                                        px: 1.2,
                                                        py: 0.3,
                                                        borderRadius: 10,
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                        backgroundColor: isAddonExcluded ? '#ffe9e9' : '#e9f8ef',
                                                        color: isAddonExcluded ? '#d32f2f' : '#2e7d32',
                                                    }}
                                                >
                                                    {isAddonExcluded ? 'Inactive' : 'Active'}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={3} md={4}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                                gap: 1,
                                            }}
                                        >
                                            <IconButton onClick={() => handleToggleAddonValueForOutlet(addon)}>
                                                {isAddonExcluded ? (
                                                    <ToggleOffOutlined sx={{ fontSize: 34, color: '#d32f2f' }} />
                                                ) : (
                                                    <ToggleOnOutlined sx={{ fontSize: 34, color: '#2e7d32' }} />
                                                )}
                                            </IconButton>

                                            <IconButton onClick={() => toggleOpen(addon._id)}>
                                                <ExpandIcon expand={openMap[addon._id]} />
                                            </IconButton>
                                        </Box>
                                    </Grid>
                                </Grid>

                                {/* Addon Values */}
                                <Grid item xs={12}>
                                    <Collapse in={openMap[addon?._id]} timeout="auto" unmountOnExit>
                                        <Box sx={{ backgroundColor: '#fafbfc', borderTop: '1px solid #edf0f5' }}>
                                            {addon?.addonValues?.map((value, valIndex) => {
                                                const isValueExcluded = value.excludedOutlets?.some(
                                                    (outlet) => outlet._id === outletId
                                                );

                                                return (
                                                    <Grid
                                                        container
                                                        key={value?._id}
                                                        sx={{
                                                            px: { xs: 1.5, md: 3 },
                                                            py: 1.5,
                                                            alignItems: 'center',
                                                            borderBottom: '1px solid #edf0f5',
                                                            '&:hover': {
                                                                backgroundColor: '#f4f6f9',
                                                            },
                                                        }}
                                                    >
                                                        <Grid item xs={2} md={2}>
                                                            <Typography sx={{ fontSize: 13, color: '#777' }}>
                                                                {index + 1}.{valIndex + 1}
                                                            </Typography>
                                                        </Grid>

                                                        <Grid item xs={7} md={6}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                                                                    {value.value}
                                                                </Typography>

                                                                {value.isDefault && (
                                                                    <Box
                                                                        sx={{
                                                                            px: 1,
                                                                            py: 0.2,
                                                                            borderRadius: 10,
                                                                            backgroundColor: '#fff4df',
                                                                            color: '#b26a00',
                                                                            fontSize: 11,
                                                                            fontWeight: 700,
                                                                        }}
                                                                    >
                                                                        Default
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </Grid>

                                                        <Grid item xs={3} md={4} sx={{ textAlign: 'right' }}>
                                                            <IconButton
                                                                onClick={() => handleToggleAddonValueForOutlet(addon, value)}
                                                            >
                                                                {isValueExcluded ? (
                                                                    <ToggleOffOutlined sx={{ fontSize: 30, color: '#d32f2f' }} />
                                                                ) : (
                                                                    <ToggleOnOutlined sx={{ fontSize: 30, color: '#2e7d32' }} />
                                                                )}
                                                            </IconButton>
                                                        </Grid>
                                                    </Grid>
                                                );
                                            })}
                                        </Box>
                                    </Collapse>
                                </Grid>
                            </Grid>
                        );
                    })
                )}

                <AddonRawMaterial />
            </Box>
        </>
    );
}

export default AddonListing;